import type { Cell, Footprint, WorldPoint } from '../types.js';

const UNIT: Footprint = { w: 1, h: 1 };

/** Logical board occupancy and cell/world conversion. */
export class Grid {
    readonly width: number;
    readonly height: number;
    readonly cellSize: number;

    private occupants: (string | null)[][];

    constructor({ width = 16, height = 16, cellSize = 1 } = {}) {
        this.width = width;
        this.height = height;
        this.cellSize = cellSize;
        this.occupants = Array.from({ length: height }, () =>
            new Array<string | null>(width).fill(null));
    }

    get worldWidth(): number {
        return this.width * this.cellSize;
    }

    get worldHeight(): number {
        return this.height * this.cellSize;
    }

    inBounds(col: number, row: number): boolean {
        return col >= 0 && row >= 0 && col < this.width && row < this.height;
    }

    cellToWorld(col: number, row: number): WorldPoint {
        return {
            x: (col + 0.5) * this.cellSize - this.worldWidth / 2,
            z: (row + 0.5) * this.cellSize - this.worldHeight / 2,
        };
    }

    footprintToWorld(col: number, row: number, footprint: Footprint = UNIT): WorldPoint {
        const anchor = this.cellToWorld(col, row);
        return {
            x: anchor.x + ((footprint.w - 1) * this.cellSize) / 2,
            z: anchor.z + ((footprint.h - 1) * this.cellSize) / 2,
        };
    }

    worldToCell(x: number, z: number): Cell | null {
        const col = Math.floor((x + this.worldWidth / 2) / this.cellSize);
        const row = Math.floor((z + this.worldHeight / 2) / this.cellSize);
        return this.inBounds(col, row) ? { col, row } : null;
    }

    isFree(col: number, row: number, footprint: Footprint = UNIT): boolean {
        for (let r = row; r < row + footprint.h; r++) {
            for (let c = col; c < col + footprint.w; c++) {
                if (!this.inBounds(c, r)) return false;
                if (this.occupants[r]![c] !== null) return false;
            }
        }
        return true;
    }

    occupy(col: number, row: number, footprint: Footprint = UNIT, occupant: string): boolean {
        if (!this.isFree(col, row, footprint)) return false;
        for (let r = row; r < row + footprint.h; r++) {
            for (let c = col; c < col + footprint.w; c++) {
                this.occupants[r]![c] = occupant;
            }
        }
        return true;
    }

    clear(col: number, row: number, footprint: Footprint = UNIT): void {
        for (let r = row; r < row + footprint.h; r++) {
            for (let c = col; c < col + footprint.w; c++) {
                if (this.inBounds(c, r)) this.occupants[r]![c] = null;
            }
        }
    }

    occupantAt(col: number, row: number): string | null {
        return this.inBounds(col, row) ? this.occupants[row]![col]! : null;
    }
}
